<template>
    <div>
        <div class="mt-4 grid w-full max-w-sm items-center gap-1.5">
            <Label for="name">
                {{
                    availabilityId !== undefined
                        ? $t("availability.edit_availability.label")
                        : $t("availability.create_availability.label")
                }}
                *</Label
            >
            <Input
                id="name"
                v-model="availabilityName"
                type="text"
                :placeholder="
                    $t('availability.create_availability.placeholder')
                "
            />

            <Label v-if="missingNameError" class="text-error-500">
                {{ $t("availability.create_availability.required") }}</Label
            >
        </div>
        <div class="mt-8">
            <PerfectScrollbar>
                <div class="max-h-[450px]">
                    <div v-for="(dayGroup, key) in groupedDays" :key="key">
                        <div
                            v-for="(day, i) in dayGroup.intervals"
                            :key="i"
                            class="mt-2 grid grid-cols-12"
                        >
                            <div
                                class="col-span-1 flex items-center justify-start"
                            >
                                <Switch
                                    v-if="i === 0"
                                    v-model="dayGroup.enabled"
                                    :class="[
                                        dayGroup.enabled
                                            ? 'bg-z-primary-600'
                                            : 'bg-iron-200',
                                        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-z-primary-600 focus:ring-offset-2',
                                    ]"
                                >
                                    <span class="sr-only">Use setting</span>
                                    <span
                                        aria-hidden="true"
                                        :class="[
                                            dayGroup.enabled
                                                ? 'translate-x-5'
                                                : 'translate-x-0',
                                            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                                        ]"
                                    />
                                </Switch>
                            </div>

                            <div
                                class="col-span-3 flex items-center justify-start pl-4"
                            >
                                <span v-if="i === 0">{{
                                    getDateNameByNumber(day.day)
                                }}</span>
                            </div>

                            <div
                                class="col-span-3 flex flex-col items-center justify-center"
                            >
                                <input
                                    :value="
                                        format(
                                            convertTimeToDate(day.start),
                                            'HH:mm',
                                        )
                                    "
                                    type="time"
                                    class="block w-full rounded-md sm:text-sm"
                                    :class="[
                                        i === failedIntervalIndex &&
                                        key === failedDayIndex
                                            ? 'border-none text-error-900 outline-none ring-1 ring-inset ring-error-500 placeholder:text-error-300 focus:ring-2 focus:ring-inset focus:ring-error-500'
                                            : 'border-iron-300 text-iron-900 shadow-sm  focus:border-z-primary-500 focus:ring-z-primary-500',
                                    ]"
                                    @change="(e) => onChangeStart(e, i, key)"
                                />
                            </div>
                            <div
                                class="col-span-1 flex items-center justify-center"
                            >
                                -
                            </div>
                            <div
                                class="col-span-3 flex flex-col items-center justify-center"
                            >
                                <input
                                    :value="
                                        format(
                                            convertTimeToDate(day.end),
                                            'HH:mm',
                                        )
                                    "
                                    type="time"
                                    class="block w-full rounded-md sm:text-sm"
                                    :class="[
                                        i === failedIntervalIndex &&
                                        key === failedDayIndex
                                            ? 'border-none text-error-900 outline-none ring-1 ring-inset ring-error-500 placeholder:text-error-300 focus:ring-2 focus:ring-inset focus:ring-error-500'
                                            : 'border-iron-300 text-iron-900 shadow-sm  focus:border-z-primary-500 focus:ring-z-primary-500',
                                    ]"
                                    @change="(e) => onChangeEnd(e, i, key)"
                                />
                            </div>

                            <div
                                class="col-span-1 flex items-center justify-center"
                            >
                                <button v-if="i === 0" @click="addDay(day.day)">
                                    <PlusIcon class="w-4" />
                                </button>
                                <button v-else @click="removeDay(day.day, i)">
                                    <TrashIcon class="w-4" />
                                </button>
                            </div>

                            <Transition mode="in-out">
                                <template
                                    v-if="
                                        i === failedIntervalIndex &&
                                        key === failedDayIndex
                                    "
                                >
                                    <div
                                        class="col-span-12 mt-2 grid grid-cols-12"
                                    >
                                        <div class="col-span-3"></div>
                                        <div
                                            class="offset col-span-9 flex w-full justify-center text-sm font-medium text-error-500"
                                        >
                                            {{
                                                $t(
                                                    "availability.invalid_interval",
                                                )
                                            }}
                                        </div>
                                    </div>
                                </template>
                            </Transition>
                        </div>
                    </div>
                </div>
            </PerfectScrollbar>
        </div>

        <slot
            name="create-button"
            :is-valid="formIsValid"
            :has-id="availabilityId !== undefined"
            :create-availability="createAvailability"
        />

        <slot
            name="edit-button"
            :has-id="availabilityId !== undefined"
            :is-valid="editIsValid"
            :update-availability="updateAvailability"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { router } from "@inertiajs/vue3";
import { Switch } from "@headlessui/vue";
import { PlusIcon, TrashIcon } from "@heroicons/vue/24/outline";
import { addHours } from "date-fns";
import { useI18n } from "vue-i18n";
import { watchDeep } from "@vueuse/core";
import { convertTimeToDate } from "@/utils/convertTimeToDate";
import { useLocalisedTzFormat } from "@/composables/useLocalisedTzFormat";
import { validateTimeInIntervals } from "../utils/validateTimeInIntervals";
import type { Days, DayKeys } from "../types/";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PerfectScrollbar } from "vue3-perfect-scrollbar";

import { watchThrottled } from "@vueuse/core";

const { format } = useLocalisedTzFormat();
const { t } = useI18n();

const props = defineProps({
    availabilityId: {
        type: Number,
        required: false,
    },
    availabilityName: {
        type: String,
        required: false,
    },
    days: {
        type: Object,
        required: false,
    },

    autosave: {
        type: Boolean,
        default: false,
    },
});

const availabilityName = ref(props.availabilityName ?? ("" as string));

const missingNameError = ref(false);

const availabilityNameCached = availabilityName.value;
const availabilityNameChanged = ref(false);

watch(
    () => availabilityName.value,
    () => {
        missingNameError.value = false;

        if (availabilityNameCached === availabilityName.value) {
            availabilityNameChanged.value = false;
        } else {
            availabilityNameChanged.value = true;
        }
    },
);

const defaultDays: Days = props.days
    ? JSON.parse(JSON.stringify(props.days))
    : {
          0: {
              intervals: [
                  {
                      key: 0,
                      day: 0,
                      startError: false,
                      endError: false,
                      start: "09:00",
                      end: "17:00",
                  },
              ],
              enabled: true,
          },
          1: {
              intervals: [
                  {
                      key: 1,
                      day: 1,
                      startError: false,
                      endError: false,
                      start: "09:00",
                      end: "17:00",
                  },
              ],
              enabled: true,
          },
          2: {
              intervals: [
                  {
                      key: 2,
                      day: 2,
                      startError: false,
                      endError: false,
                      start: "09:00",
                      end: "17:00",
                  },
              ],
              enabled: true,
          },
          3: {
              intervals: [
                  {
                      key: 3,
                      day: 3,
                      startError: false,
                      endError: false,
                      start: "09:00",
                      end: "17:00",
                  },
              ],
              enabled: true,
          },
          4: {
              intervals: [
                  {
                      key: 4,
                      day: 4,
                      startError: false,
                      endError: false,
                      start: "09:00",
                      end: "17:00",
                  },
              ],
              enabled: true,
          },
          5: {
              intervals: [
                  {
                      key: 5,
                      day: 5,
                      startError: false,
                      endError: false,
                      start: "09:00",
                      end: "17:00",
                  },
              ],
              enabled: false,
          },
          6: {
              intervals: [
                  {
                      key: 6,
                      day: 6,
                      startError: false,
                      endError: false,
                      start: "09:00",
                      end: "17:00",
                  },
              ],
              enabled: false,
          },
      };
const groupedDays = ref(defaultDays);

watchThrottled(
    [availabilityName],
    () => {
        if (props.autosave) {
            if (availabilityName.value.length === 0) {
                missingNameError.value = true;
                return;
            }

            if (editIsValid.value) {
                updateAvailability();
            }
        }
    },
    {
        throttle: 500,
    },
);
//To edit perform validation on the form
const cachedGroupDays = JSON.stringify(groupedDays.value);

const groupDaysChanged = ref(false);
const formIsValid = ref(true);
const failedIntervalIndex = ref();
const failedDayIndex = ref();

const emit = defineEmits<{
    "on-finish": [{ created: boolean }];
}>();

watchDeep(groupedDays, () => {
    if (JSON.stringify(groupedDays.value) === cachedGroupDays) {
        groupDaysChanged.value = false;
    } else {
        groupDaysChanged.value = true;
    }

    if (props.autosave) {
        if (availabilityName.value.length === 0) {
            missingNameError.value = true;
            return;
        }

        if (editIsValid.value) {
            updateAvailability();
        }
    }
});

const createAvailability = function () {
    missingNameError.value = false;

    if (availabilityName.value.length === 0) {
        missingNameError.value = true;
        return;
    }

    router.post(
        "/availabilities",
        {
            name: availabilityName.value,
            days: groupedDays.value,
        },
        {
            onSuccess: () => {
                onFinish(true);
            },
        },
    );
};

const updateAvailability = function () {
    missingNameError.value = false;

    if (availabilityName.value.length === 0) {
        missingNameError.value = true;
        return;
    }
    router.put(
        "/availabilities/" + props.availabilityId,
        {
            name: availabilityName.value,
            days: groupedDays.value,
        },
        {
            onFinish: () => {
                onFinish();
            },
            preserveScroll: true,
        },
    );
};

const onFinish = (created = false) => {
    emit("on-finish", { created });
};

const addDay = (day: DayKeys) => {
    if (groupedDays.value[day].intervals.length > 0) {
        // get previous time interval if it exists
        const currentDay = groupedDays.value[day];

        // get the end time of the previous time interval
        const date = convertTimeToDate(
            currentDay.intervals[currentDay.intervals.length - 1].end,
        );

        const newStart = date;
        const newEnd = addHours(date, 1);

        groupedDays.value[day].intervals.push({
            day: day,
            key: day,
            endError: false,
            startError: false,
            start: format(newStart, "HH:mm"),
            end: format(newEnd, "HH:mm"),
        });
    } else {
        groupedDays.value[day].intervals.push({
            day: day,
            key: day,
            endError: false,
            startError: false,
            start: "09:00",
            end: "17:00",
        });
    }
};

const getDateNameByNumber = function (dayNumber: number): string {
    if (dayNumber === 0) {
        return t("availability.monday");
    }
    if (dayNumber === 1) {
        return t("availability.tuesday");
    }
    if (dayNumber === 2) {
        return t("availability.wednesday");
    }
    if (dayNumber === 3) {
        return t("availability.thursday");
    }
    if (dayNumber === 4) {
        return t("availability.friday");
    }
    if (dayNumber === 5) {
        return t("availability.saturday");
    }
    if (dayNumber === 6) {
        return t("availability.sunday");
    }

    return "";
};

const removeDay = function (day: DayKeys, key: number) {
    groupedDays.value[day].intervals = groupedDays.value[day].intervals.filter(
        (_, keyInArray) => {
            return key !== keyInArray;
        },
    );
};

function onChangeStart(e: Event, index: number, groupDaysIndex: DayKeys) {
    failedIntervalIndex.value = null;
    failedDayIndex.value = null;

    const target = e.target as HTMLInputElement;

    groupedDays.value[groupDaysIndex].intervals[index].start = target.value;

    const { isValid, failedIndex } = validateTimeInIntervals(
        groupedDays.value[groupDaysIndex].intervals,
    );

    if (!isValid) {
        failedIntervalIndex.value = failedIndex;
        failedDayIndex.value = groupDaysIndex;
    }

    formIsValid.value = isValid;
}

function onChangeEnd(e: Event, index: number, groupDaysIndex: DayKeys) {
    failedIntervalIndex.value = null;
    failedDayIndex.value = null;

    const target = e.target as HTMLInputElement;

    groupedDays.value[groupDaysIndex].intervals[index].end = target.value;

    const { isValid, failedIndex } = validateTimeInIntervals(
        groupedDays.value[groupDaysIndex].intervals,
    );

    if (!isValid) {
        failedIntervalIndex.value = failedIndex;
        failedDayIndex.value = groupDaysIndex;
    }

    formIsValid.value = isValid;
}

const editIsValid = computed(() => {
    return formIsValid.value;
});
</script>
